import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { authService } from "@/services/auth.service";
import { tokenManager } from "@/utils/token-manager";
import { FEATURES } from "@/config";
import type { AuthUser, LoginRequest, RegisterRequest } from "@/types/api";
import { ROOT } from "@/routes";
import * as Sentry from "@sentry/react";
import { analytics } from "@/services/analytics.service";
import { clearResumeQueryCache, restoreResumeQueryCache } from "@/lib/query/resumeQueryCache";
import { cachePersister } from "@/lib/cachePersistence";
import { buildBootstrapAuthState, shouldClearBootstrapSession, type BootstrapAuthState } from "@/contexts/authBootstrap";

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: AuthUser | null;
  login: (credentials: LoginRequest | { user: AuthUser; access_token: string; refresh_token: string }) => Promise<void>;
  register: (data: RegisterRequest) => Promise<{ user: AuthUser; access_token: string; refresh_token: string }>;
  logout: (redirectPath?: string) => Promise<void>;
  refreshAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const restoreBootstrapResumeCache = (
  queryClient: QueryClient,
  userId: string | null | undefined,
) => {
  if (!userId) return;
  restoreResumeQueryCache(queryClient, userId);
};

const readBootstrapAuthState = (queryClient: QueryClient): BootstrapAuthState => {
  try {
    if (FEATURES.USE_MOCK_AUTH) {
      const mockAuth = localStorage.getItem("mock_auth");
      if (mockAuth === "true") {
        const mockUser: AuthUser = {
          user_id: "mock-user-123",
          email: "demo@stasht.com",
          sender_ids: ["mock-sender-1", "mock-sender-2"],
          is_admin: false,
          is_gated: false,
          activation_stage: "active",
          setup_complete: true,
          onboarding_progress: {
            setup_stasht_completed: true,
          },
        };
        restoreBootstrapResumeCache(queryClient, mockUser.user_id);
        return {
          user: mockUser,
          isAuthenticated: true,
          canSkipLoading: true,
        };
      }

      return {
        user: null,
        isAuthenticated: false,
        canSkipLoading: false,
      };
    }

    authService.initialize();
    const cachedUser = authService.getCurrentUser();
    const bootstrapState = buildBootstrapAuthState<AuthUser>({
      cachedUser,
      hasSessionMarker: tokenManager.hasSessionMarker(),
    });
    if (bootstrapState.canSkipLoading) {
      restoreBootstrapResumeCache(queryClient, cachedUser?.user_id);
    }

    return bootstrapState;
  } catch {
    return {
      user: null,
      isAuthenticated: false,
      canSkipLoading: false,
    };
  }
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const queryClient = useQueryClient();
  const bootstrapRef = useRef<BootstrapAuthState | null>(null);
  if (bootstrapRef.current === null) {
    bootstrapRef.current = readBootstrapAuthState(queryClient);
  }
  const bootstrap = bootstrapRef.current;
  const [isAuthenticated, setIsAuthenticated] = useState(bootstrap.isAuthenticated);
  const [isLoading, setIsLoading] = useState(!bootstrap.canSkipLoading);
  const [user, setUser] = useState<AuthUser | null>(bootstrap.user);

  // Initialize auth state.
  // Auth gate: if a valid refresh token exists, the user is authenticated — render
  // the app immediately from cached user data. Access token refresh happens lazily
  // via the API client's 401→refresh→retry flow (client.ts:149). This mirrors
  // Auth0 CredentialsManager.hasValid() and how Spotify/Netflix/Instagram work:
  // never gate app startup on a network call.
  useEffect(() => {
    const initAuth = async () => {
      try {
        if (FEATURES.USE_MOCK_AUTH) {
          // Check mock auth state
          const mockAuth = localStorage.getItem("mock_auth");
          if (mockAuth === "true") {
            const mockUser: AuthUser = {
              user_id: "mock-user-123",
              email: "demo@stasht.com",
              sender_ids: ["mock-sender-1", "mock-sender-2"],
              is_admin: false,
              is_gated: false,
              activation_stage: "active",
              setup_complete: true,
              onboarding_progress: {
                setup_stasht_completed: true,
              },
            };
            restoreResumeQueryCache(queryClient, mockUser.user_id);
            setUser(mockUser);
            setIsAuthenticated(true);
            Sentry.setUser({ id: mockUser.user_id });
            analytics.identify(mockUser.user_id, { email: mockUser.email });
          }
        } else {
          await tokenManager.hydrate();
          authService.initialize();

          if (tokenManager.hasValidSession()) {
            const cachedUser = authService.getCurrentUser();

            if (cachedUser) {
              // Happy path: instant auth from cache, no network call
              if (!bootstrap.canSkipLoading) {
                restoreResumeQueryCache(queryClient, cachedUser.user_id);
              }
              setUser(cachedUser);
              setIsAuthenticated(true);
              Sentry.setUser({ id: cachedUser.user_id });
              analytics.identify(cachedUser.user_id, { email: cachedUser.email });

              // Background: refresh access token + fetch fresh profile.
              // Non-blocking — stale cached data is acceptable briefly.
              tokenManager.ensureFreshAccessToken()
                .then(async (token) => {
                  if (!token) return;
                  try {
                    const freshUser = await authService.checkUserStatus();
                    if (freshUser) {
                      setUser(freshUser);
                      Sentry.setUser({ id: freshUser.user_id });
                      analytics.identify(freshUser.user_id, { email: freshUser.email });
                    }
                  } catch {
                    // Profile fetch failed — cached user is fine for now
                  }
                })
                .catch(() => {
                  // Token refresh failed — API client handles 401 on next real request
                });
            } else {
              // Edge case: tokens in Keychain but localStorage was cleared by iOS.
              // Must fetch profile before rendering (components need user object).
              // PageAuthLoader shows during this — NOT the login screen.
              try {
                await tokenManager.ensureFreshAccessToken();
                const freshUser = await authService.checkUserStatus();
                if (freshUser) {
                  restoreResumeQueryCache(queryClient, freshUser.user_id);
                  setUser(freshUser);
                  setIsAuthenticated(true);
                  Sentry.setUser({ id: freshUser.user_id });
                  analytics.identify(freshUser.user_id, { email: freshUser.email });
                }
              } catch {
                // Can't recover right now — tokens preserved for next cold start
              }
            }
          } else if (shouldClearBootstrapSession({
            hasValidSession: false,
            bootstrapCanSkipLoading: bootstrap.canSkipLoading,
          })) {
            await authService.clearLocalSession();
            clearResumeQueryCache(bootstrap.user?.user_id);
            queryClient.clear();
            setUser(null);
            setIsAuthenticated(false);
            Sentry.setUser(null);
            analytics.reset();
          }
          // If !hasValidSession(): no valid refresh token, user is genuinely not logged in.
          // isLoading=false in finally → RequireAuth redirects to login.
        }
      } catch (error) {
        // Silent fail - validation errors are handled by UI
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, [bootstrap.canSkipLoading, bootstrap.user?.user_id, queryClient]);

  const login = async (credentials: LoginRequest | { user: AuthUser; access_token: string; refresh_token: string }) => {
    // Check if it's a direct token login (pre-issued tokens)
    if ("access_token" in credentials && "refresh_token" in credentials) {
      // Direct login with tokens
      await tokenManager.storeTokens(credentials.access_token, credentials.refresh_token);
      authService.setCurrentUser(credentials.user);
      setUser(credentials.user);
      setIsAuthenticated(true);
      // Set Sentry user context
      Sentry.setUser({
        id: credentials.user.user_id,
      });
      analytics.identify(credentials.user.user_id, { email: credentials.user.email });
      return;
    }

    // Regular email/password login
    if (FEATURES.USE_MOCK_AUTH) {
      // Mock login - always succeeds
      const mockUser: AuthUser = {
        user_id: "mock-user-123",
        email: credentials.email,
        sender_ids: ["mock-sender-1", "mock-sender-2"],
        is_admin: credentials.email.includes("admin"),
        is_gated: false,
        activation_stage: "active",
        setup_complete: true,
        onboarding_progress: {
          setup_stasht_completed: true,
        },
      };
      setUser(mockUser);
      setIsAuthenticated(true);
      localStorage.setItem("mock_auth", "true");
      // Set Sentry user context
      Sentry.setUser({
        id: mockUser.user_id,
      });
      analytics.identify(mockUser.user_id, { email: mockUser.email });
      return;
    }

    try {
      const authUser = await authService.login(credentials as LoginRequest);
      setUser(authUser);
      setIsAuthenticated(true);
      // Set Sentry user context
      Sentry.setUser({
        id: authUser.user_id,
      });
      analytics.identify(authUser.user_id, { email: authUser.email });
    } catch (error) {
      setUser(null);
      setIsAuthenticated(false);
      throw error;
    }
  };

  const register = async (data: RegisterRequest) => {
    const result = await authService.register(data);
    return result;
  };

  const logout = async (redirectPath?: string) => {
    const previousUserId = user?.user_id;

    if (FEATURES.USE_MOCK_AUTH) {
      localStorage.removeItem("mock_auth");
    } else {
      await authService.logout();
    }

    // Clear all cached data from previous user, including the durable caches:
    // the resume cache and the on-disk unit cache (which is not user-scoped).
    clearResumeQueryCache(previousUserId);
    queryClient.clear();
    await cachePersister.removeClient();

    setUser(null);
    setIsAuthenticated(false);

    // Clear admin impersonation permission cache
    localStorage.removeItem("stasht_impersonate_permission");

    // Clear Sentry user context
    Sentry.setUser(null);
    analytics.reset();

    // Account linking can return to sign-in after the same complete cache reset.
    window.location.href = typeof redirectPath === "string" && redirectPath.startsWith("/") && !redirectPath.startsWith("//")
      ? redirectPath : ROOT();
  };

  const refreshAuth = async () => {
    try {
      const updatedUser = await authService.checkUserStatus();
      if (updatedUser) {
        setUser(updatedUser);
        setIsAuthenticated(true);
        Sentry.setUser({ id: updatedUser.user_id });
        analytics.identify(updatedUser.user_id, { email: updatedUser.email });
      }
    } catch (error) {
      logout();
      throw error;
    }
  };

  // Refresh stale cached user fields (e.g. is_gated from before migration).
  // Token refresh is NOT done here — it's handled lazily by the API client (client.ts:149).
  useEffect(() => {
    if (!isAuthenticated || !user) return;

    const refreshStaleProfile = async () => {
      try {
        if (user.is_gated === undefined) {
          console.log("[AuthContext] Refreshing user — is_gated missing from cached data");
          const updatedUser = await authService.checkUserStatus();
          if (updatedUser) {
            setUser(updatedUser);
            Sentry.setUser({ id: updatedUser.user_id });
            analytics.identify(updatedUser.user_id, { email: updatedUser.email });
          }
        }
      } catch (error) {
        // Network errors are fine — cached data works for now
        console.debug("Profile refresh failed:", error);
      }
    };

    refreshStaleProfile();
  }, [isAuthenticated, user?.user_id, user?.is_gated]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        user,
        login,
        register,
        logout,
        refreshAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
